import { Preferences } from '@capacitor/preferences'
import { AccessControl, NativeBiometric } from '@capgo/capacitor-native-biometric'

const migrationKey = 'biometricRefreshTokenMigration'
const protectedMigrationState = 'protected'
const attemptedMigrationState = 'attempted'

const getLegacyServer = () => process.env.APP_ID
const getProtectedServer = () => `${getLegacyServer()}:biometric-v2`

const getRefreshToken = (credentials) => {
  if (!credentials || !credentials.password) {
    throw new Error('Missing biometric refresh token')
  }

  return credentials.password
}

const writeProtectedRefreshToken = async (refreshToken) => {
  await NativeBiometric.setCredentials({
    username: 'refresh_token',
    password: refreshToken,
    server: getProtectedServer(),
    accessControl: AccessControl.BIOMETRY_CURRENT_SET,
    title: 'Authenticate'
  })
}

export const readBiometricRefreshToken = async () => {
  const { value: migrationState } = await Preferences.get({ key: migrationKey })

  if (migrationState === protectedMigrationState) {
    const credentials = await NativeBiometric.getSecureCredentials({
      server: getProtectedServer(),
      reason: 'Authenticate',
      title: 'Authenticate'
    })

    return {
      isLegacyMigration: false,
      refreshToken: getRefreshToken(credentials)
    }
  }

  if (migrationState === attemptedMigrationState) {
    await Preferences.set({ key: 'biometricPreference', value: 'false' })
    throw new Error('Legacy biometric migration is unavailable')
  }

  await NativeBiometric.verifyIdentity({
    reason: 'Authenticate',
    title: 'Authenticate'
  })
  await Preferences.set({ key: migrationKey, value: attemptedMigrationState })
  let credentials
  try {
    credentials = await NativeBiometric.getCredentials({ server: getLegacyServer() })
  } catch (error) {
    try {
      await NativeBiometric.deleteCredentials({ server: getLegacyServer() })
    } finally {
      await Preferences.set({ key: 'biometricPreference', value: 'false' })
    }
    throw error
  }

  return {
    isLegacyMigration: true,
    refreshToken: getRefreshToken(credentials)
  }
}

export const completeLegacyBiometricMigration = async (refreshToken) => {
  await NativeBiometric.updateSecureCredentials({
    username: 'refresh_token',
    password: refreshToken,
    server: getProtectedServer()
  })
  await NativeBiometric.deleteCredentials({ server: getLegacyServer() })
  await Preferences.set({ key: migrationKey, value: protectedMigrationState })
}

export const saveProtectedBiometricRefreshToken = async (refreshToken) => {
  await NativeBiometric.updateSecureCredentials({
    username: 'refresh_token',
    password: refreshToken,
    server: getProtectedServer()
  })
  await Preferences.set({ key: migrationKey, value: protectedMigrationState })
}

export const enableBiometricRefreshToken = async (refreshToken) => {
  await writeProtectedRefreshToken(refreshToken)
  await NativeBiometric.deleteCredentials({ server: getLegacyServer() })
  await Preferences.set({ key: migrationKey, value: protectedMigrationState })
  await Preferences.set({ key: 'biometricPreference', value: 'true' })
}

export const clearBiometricRefreshToken = async () => {
  let cleanupError

  try {
    await NativeBiometric.deleteCredentials({ server: getLegacyServer() })
  } catch (error) {
    cleanupError = error
  }

  try {
    await NativeBiometric.deleteCredentials({ server: getProtectedServer() })
  } catch (error) {
    cleanupError = cleanupError || error
  }

  await Preferences.remove({ key: migrationKey })

  if (cleanupError) {
    throw cleanupError
  }
}

export const disableBiometricRefreshToken = async (preference = 'false') => {
  let cleanupError

  try {
    await clearBiometricRefreshToken()
  } catch (error) {
    cleanupError = error
  }

  await Preferences.set({ key: 'biometricPreference', value: preference })

  if (cleanupError) {
    throw cleanupError
  }
}
